import { ref } from 'vue'
import { defineStore } from 'pinia'
import { formatISO } from 'date-fns'
import { toCamelCase } from '@/shared/utils/format'
import { GOTO_KEY, MOTION_BOXES, MY_SELECTION } from '../constants/query.constants'
import { GotoType } from '../types/query.enums'
import type { LocationQuery, LocationQueryRaw, RouteLocationRaw } from 'vue-router'
import type { QueryParams } from '../models/query.model'

export const useQueryStore = defineStore('query', () => {
  const queryParams = ref<QueryParams>({})

  const getAccountKey = (): string | undefined => {
    let value

    if (queryParams.value.type === GotoType.Filter) {
      value = queryParams.value.accountKey
    }

    return value ? value.toString().toLowerCase() : undefined
  }

  const getViewName = (): string | undefined => {
    let value

    if (queryParams.value.type === GotoType.Filter) {
      value = queryParams.value.appViewName
    }

    return value ? value.toString().toLowerCase() : undefined
  }

  const storeGotoRoute = (path: string, query: LocationQuery) => {
    if (query.goto) localStorage.setItem(GOTO_KEY, JSON.stringify({ path: path, query: query }))
  }

  const removeGotoRoute = (): void => localStorage.removeItem(GOTO_KEY)

  const getDate = (date: string): string | undefined =>
    date ? formatISO(new Date(date)) : undefined

  const parseQueryParams = (query: Record<string, unknown>): QueryParams => {
    if (query.goto === MOTION_BOXES && typeof query.shipmentNo === 'string') {
      return {
        type: GotoType.Shipment,
        shipmentNo: query.shipmentNo,
      }
    }

    if (query.goto === MY_SELECTION) {
      return {
        type: GotoType.Filter,
        accountKey: query.accountKey as string,
        appViewName: query.appViewName as string,
        fromViewDate: getDate(query.fromViewDate as string),
        toViewDate: getDate(query.toViewDate as string),
        fromActionDate: getDate(query.fromActionDate as string),
        toActionDate: getDate(query.toActionDate as string),
        carrierName: query.carrierName as string,
        countryName: query.countryName as string,
        serviceGroupName: query.serviceGroupName as string,
        zipZoneName: query.zipZoneName as string,
        dimension1Value: query.dimension1Value as string,
        dimension2Value: query.dimension2Value as string,
        dimension3Value: query.dimension3Value as string,
        actionId: parseInt(query.actionId as string),
        status: query.status as string | string[],
        isAssignedToMe: query.myShipments === 'true',
      }
    }

    return {}
  }

  const parseRoute = (
    path: string,
    query: LocationQuery,
  ): { path: string; query: unknown } | void => {
    const normalized: Record<string, unknown> = {}

    for (const key in query) {
      normalized[toCamelCase(key)] = query[key]
    }

    queryParams.value = parseQueryParams(normalized)
    storeGotoRoute(path, query)
  }
  const getGotoRoute = (): RouteLocationRaw | null => {
    const goto = localStorage.getItem(GOTO_KEY)

    if (!goto) return null

    const parsed = JSON.parse(goto)

    if (!parsed?.path) return null

    const route: RouteLocationRaw = {
      path: parsed.path,
      query: (parsed.query ?? undefined) as LocationQueryRaw | undefined,
    }

    removeGotoRoute()

    return route
  }

  return {
    queryParams,
    parseRoute,
    getAccountKey,
    getViewName,
    getGotoRoute,
    removeGotoRoute,
  }
})
